import type { AgeBand, LockedSummary, PremiumReport, StartTestResponse, SubmitAnswerPayload, SubmitAnswerResponse } from "../../shared/types";
import { getAnalyticsSessionId } from "./analyticsClient";

async function readJson<T>(response: Response): Promise<T> {
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const error = data && typeof data === "object" && "error" in data ? (data.error as unknown) : null;
    const message =
      error && typeof error === "object" && "message" in error
        ? String((error as { message: unknown }).message)
        : typeof error === "string"
          ? error
          : "API error";
    throw new Error(message);
  }
  return data as T;
}

export async function startTest(ageBand: AgeBand) {
  const response = await fetch("/api/test/start", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ mode: "standard", ageBand, analyticsSessionId: getAnalyticsSessionId() })
  });
  return readJson<StartTestResponse>(response);
}

export async function submitAnswer(attemptId: string, payload: SubmitAnswerPayload) {
  const response = await fetch(`/api/test/${attemptId}/answer`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload)
  });
  return readJson<SubmitAnswerResponse>(response);
}

export async function getLockedSummary(attemptId: string) {
  const response = await fetch(`/api/test/${attemptId}/locked-summary`);
  return readJson<LockedSummary>(response);
}

export async function getDevPremiumReport(attemptId: string) {
  const response = await fetch(`/api/dev/report/${attemptId}`);
  return readJson<PremiumReport>(response);
}

export async function getDevDemoPremiumReport() {
  const response = await fetch("/api/dev/report/demo");
  return readJson<PremiumReport>(response);
}

export async function getPremiumReport(reportId: string, token: string) {
  const params = new URLSearchParams({ token });
  const response = await fetch(`/api/reports/${encodeURIComponent(reportId)}?${params.toString()}`);
  return readJson<PremiumReport>(response);
}

export async function getPremiumReportRenderData(reportId: string, credential: string) {
  const params = new URLSearchParams({ credential });
  const response = await fetch(`/api/reports/${encodeURIComponent(reportId)}/render-data?${params.toString()}`);
  return readJson<PremiumReport>(response);
}

export async function getAdminReportPreviewRenderData(attemptId: string, credential: string) {
  const params = new URLSearchParams({ credential });
  const response = await fetch(`/api/admin/attempts/${encodeURIComponent(attemptId)}/report-preview/render-data?${params.toString()}`);
  return readJson<PremiumReport>(response);
}

export async function getPremiumReportPdf(reportId: string, token: string) {
  const params = new URLSearchParams({ token });
  const response = await fetch(`/api/reports/${encodeURIComponent(reportId)}/pdf?${params.toString()}`);
  if (!response.ok) {
    throw new Error("Could not generate the PDF right now.");
  }
  return response.blob();
}

export async function createCheckoutSession(attemptId: string, email?: string) {
  const response = await fetch("/api/payments/create-checkout-session", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ attemptId, email: email?.trim() || undefined, analyticsSessionId: getAnalyticsSessionId() })
  });
  return readJson<{ checkoutUrl: string }>(response);
}

export async function getCheckoutStatus(sessionId: string) {
  const params = new URLSearchParams({ session_id: sessionId });
  const response = await fetch(`/api/payments/checkout-status?${params.toString()}`);
  return readJson<{ status: "paid"; reportUrl: string } | { status: "pending" } | { status: "not_found" }>(response);
}
